import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Skeleton,
} from "@mui/material";
import { EmojiObjects, Close as CloseIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import LoadingButton from "../../components/LoadingButton";
import axios from "axios";
import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { CONTENT_TYPE_OPTIONS } from "../../constants/contentTypes";
import { hostname, parseError } from "../../utils";

export default function ContentFromIdeaDialog({
  open,
  onClose,
  getToken,
  companyId,
  onSuccess,
  onContentCreated,
}: {
  open: boolean;
  onClose: () => void;
  getToken: Function;
  companyId: string;
  onSuccess: () => void;
  onContentCreated?: (content: any) => void;
}) {
  const [idea, setIdea] = useState("");
  const [contentType, setContentType] = useState("blog_post");
  const [funnelStage, setFunnelStage] = useState("tofu");
  const [brandVoiceId, setBrandVoiceId] = useState("");
  const [icpId, setIcpId] = useState("");
  const [brandImageId, setBrandImageId] = useState("");
  const [publishDate, setPublishDate] = useState(dayjs());
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // Data for dropdowns
  const [brandVoices, setBrandVoices] = useState<any[]>([]);
  const [icpProfiles, setIcpProfiles] = useState<any[]>([]);
  const [brandImages, setBrandImages] = useState<any[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);

  const contentTypes = CONTENT_TYPE_OPTIONS;

  const funnelStages = [
    { value: "tofu", label: "TOFU (Top of Funnel)" },
    { value: "mofu", label: "MOFU (Middle of Funnel)" },
    { value: "bofu", label: "BOFU (Bottom of Funnel)" },
  ];

  // Fetch brand voices when dialog opens
  useEffect(() => {
    if (open) {
      fetchBrandVoices();
      fetchIcpProfiles();
      fetchBrandImages();
    }
  }, [open, companyId]);

  const fetchBrandVoices = async () => {
    setLoadingVoices(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/brand-voices`, {
        params: { companyId, page: 1, pageSize: 100 },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setBrandVoices(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching brand voices:", error);
    } finally {
      setLoadingVoices(false);
    }
  };

  const fetchIcpProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/profiles`, {
        params: { companyId, page: 1, pageSize: 100 },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setIcpProfiles(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching ICP profiles:", error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const fetchBrandImages = async () => {
    setLoadingImages(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/brand-images`, {
        params: { companyId, page: 1, pageSize: 100 },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setBrandImages(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching brand images:", error);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleGenerate = async () => {
    if (!idea.trim()) {
      setError("Please enter an idea");
      return;
    }

    setGenerating(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/generate-from-idea`,
        {
          idea,
          contentType,
          funnelStage,
          companyId,
          brandVoiceId,
          icpId,
          brandImageId,
          publishDate: publishDate ? publishDate.toISOString() : null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        onSuccess();
        // Open content preview if callback provided
        if (onContentCreated && response.data.content) {
          onContentCreated(response.data.content);
        }
        onClose();
      }
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (!generating) {
      setIdea("");
      setContentType("blog_post");
      setFunnelStage("tofu");
      setBrandVoiceId("");
      setIcpId("");
      setBrandImageId("");
      setPublishDate(dayjs());
      setError("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EmojiObjects color="primary" />
          Create Content from Idea
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          <Alert severity="info">
            Enter your content idea below. Our AI will help you develop it into 
            a complete content piece tailored to your selected content type and 
            funnel stage. Optionally select a brand voice and target customer 
            profile to further customize the generated content.
          </Alert>

          <TextField
            label="Your Content Idea"
            multiline
            rows={4}
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            placeholder="Example: A comprehensive guide about the benefits of AI in modern marketing, focusing on automation and personalization..."
            fullWidth
            required
            disabled={generating}
            helperText="Be as specific as possible. Include key points, target audience, and desired outcomes."
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                label="Content Type"
                disabled={generating}
              >
                {contentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Funnel Stage</InputLabel>
              <Select
                value={funnelStage}
                onChange={(e) => setFunnelStage(e.target.value)}
                label="Funnel Stage"
                disabled={generating}
              >
                {funnelStages.map((stage) => (
                  <MenuItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Brand Voice (Optional)</InputLabel>
              {loadingVoices ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <Select
                  value={brandVoiceId}
                  onChange={(e) => setBrandVoiceId(e.target.value)}
                  label="Brand Voice (Optional)"
                  disabled={generating}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {brandVoices.length === 0 ? (
                    <MenuItem disabled>
                      <em>No brand voices available</em>
                    </MenuItem>
                  ) : (
                    brandVoices.map((voice) => (
                      <MenuItem key={voice.id} value={voice.id}>
                        {voice.name || "Unnamed Voice"}
                      </MenuItem>
                    ))
                  )}
                </Select>
              )}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Target Customer (Optional)</InputLabel>
              {loadingProfiles ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <Select
                  value={icpId}
                  onChange={(e) => setIcpId(e.target.value)}
                  label="Target Customer (Optional)"
                  disabled={generating}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {icpProfiles.length === 0 ? (
                    <MenuItem disabled>
                      <em>No target customers available</em>
                    </MenuItem>
                  ) : (
                    icpProfiles.map((profile) => (
                      <MenuItem key={profile.id} value={profile.id}>
                        {profile.name || "Unnamed Profile"}
                      </MenuItem>
                    ))
                  )}
                </Select>
              )}
            </FormControl>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Brand Image (Optional)</InputLabel>
            {loadingImages ? (
              <Skeleton variant="rectangular" height={56} />
            ) : (
              <Select
                value={brandImageId}
                onChange={(e) => setBrandImageId(e.target.value)}
                label="Brand Image (Optional)"
                disabled={generating}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {brandImages.length === 0 ? (
                  <MenuItem disabled>
                    <em>No brand images available</em>
                  </MenuItem>
                ) : (
                  brandImages.map((image) => (
                    <MenuItem key={image.id} value={image.id}>
                      {image.name || "Unnamed Image"}
                    </MenuItem>
                  ))
                )}
              </Select>
            )}
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Intended Publish Date"
              value={publishDate}
              onChange={(newValue) => setPublishDate(newValue ? dayjs(newValue.toISOString()) : dayjs())}
              disabled={generating}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: "Select when you plan to publish this content. The AI will consider seasonal relevance and upcoming events."
                }
              }}
            />
          </LocalizationProvider>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            The AI will create a complete content piece based on your idea, 
            appropriate for the selected content type and funnel stage. 
            {brandVoices.length === 0 && icpProfiles.length === 0 && (
              <>
                {" "}Consider creating a brand voice or target customer profile 
                for more customized content generation.
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={generating}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleGenerate}
          variant="contained"
          loading={generating}
          disabled={!idea.trim()}
          startIcon={<EmojiObjects />}
        >
          Generate Content
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}